<template>
  <div class="course-details-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">{{ $t("common.loading") }}</p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="course-content-wrapper">
      <input
        type="hidden"
        id="videoCurrentTime"
        name="videoCurrentTime"
        value="0"
      />

      <!-- Hero Section -->
      <div class="course-hero">
        <div class="hero-background">
          <img
            :src="`${courseDetails.course.preview_image}`"
            @error="setDefaultImage"
            :alt="$t('course.thumbnail_alt')"
            class="hero-image"
          />
          <div class="hero-overlay"></div>
        </div>
        <div class="hero-content container">
          <div class="hero-info">
            <div class="course-badges-row">
              <div class="course-category-badge">
                <i class="fas fa-folder"></i>
                {{ courseDetails.course.category.title }}
              </div>
              <router-link
                v-if="courseDetails.course.institute && courseDetails.course.institute.id"
                :to="{
                  path: '/courses',
                  query: {
                    institute_id: courseDetails.course.institute.id,
                    title: courseDetails.course.institute.title,
                  },
                }"
                class="course-institute-badge"
              >
                <i class="fas fa-building"></i>
                {{ courseDetails.course.institute.title }}
              </router-link>
              <!-- QBank Badge with Link -->
              <router-link
                v-if="courseDetails.course.subscription_plan_id"
                :to="{
                  path: '/qbank',
                  query: {
                    subscription_id: courseDetails.course.subscription_plan_id,
                  },
                }"
                class="course-qbank-badge"
              >
                <i class="fas fa-question-circle"></i>
                {{ $t("course.qbank") || "بنك الأسئلة" }}
              </router-link>
            </div>
            <h1 class="course-title">{{ courseDetails.course.title }}</h1>
            <p class="course-description">
              {{ courseDetails.course.short_detail }}
            </p>

            <div class="course-meta">
              <div class="meta-item">
                <i class="fas fa-play-circle"></i>
                <span>{{ totalClasses }} {{ $t("course.lectures") }}</span>
              </div>
              <div class="meta-item">
                <i class="fas fa-book"></i>
                <span>{{ totalChapters }} {{ $t("course.chapters") }}</span>
              </div>
              <div class="meta-item">
                <i class="fas fa-clock"></i>
                <span>{{
                  formattedDuration(courseDetails.course.duration)
                }}</span>
              </div>
            </div>

            <div class="hero-actions">
              <a
                href="#"
                data-bs-toggle="modal"
                data-bs-target="#share-modal"
                class="share-btn"
              >
                <i class="bi bi-share"></i>
                {{ $t("bundle.share_in_app") }}
              </a>
              <a
                href="#"
                data-bs-toggle="modal"
                data-bs-target="#share-web-modal"
                class="share-btn share-web"
              >
                <i class="bi bi-globe"></i>
                {{ $t("bundle.share_on_web") }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="course-main-content container">
        <div class="content-grid">
          <!-- Sidebar -->
          <div class="course-sidebar">
            <!-- Price Card -->
            <div class="price-card">
              <div class="price-header">
                <!-- Enrollment Check Loader -->
                <div v-if="checkingEnrollment" class="enrollment-loader">
                  <div class="enrollment-spinner">
                    <div class="spinner-dot"></div>
                    <div class="spinner-dot"></div>
                    <div class="spinner-dot"></div>
                  </div>
                  <span class="checking-text">{{
                    $t("course.checking_enrollment")
                  }}</span>
                </div>
                <!-- Not Enrolled - Show Price -->
                <div
                  v-else-if="!isEnrolled(courseDetails.course.id)"
                  class="price-display"
                >
                  <template
                    v-if="
                      (courseDetails.course.price === null ||
                        courseDetails.course.price === 0) &&
                      (courseDetails.course.discount_price === null ||
                        courseDetails.course.discount_price === 0)
                    "
                  >
                    <span class="price-free">{{ $t("course.free") }}</span>
                  </template>
                  <template v-else-if="courseDetails.course.discount_price">
                    <span class="price-current"
                      >{{ courseDetails.course.discount_price }}
                      {{ $appCurrency }}</span
                    >
                    <span class="price-old"
                      >{{ courseDetails.course.price }} {{ $appCurrency }}</span
                    >
                    <span class="discount-badge">
                      {{
                        calculateDiscountPercentage(
                          courseDetails.course.price,
                          courseDetails.course.discount_price
                        )
                      }}% {{ $t("course.discount") }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="price-current"
                      >{{ courseDetails.course.price }} {{ $appCurrency }}</span
                    >
                  </template>
                </div>
                <!-- Enrolled Badge -->
                <div v-else class="enrolled-badge">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ $t("course.enrolled") }}</span>
                </div>
              </div>

              <!-- Course Chat Group Button - Only for enrolled students -->
              <div v-if="isEnrolled(courseDetails.course.id) && !checkingEnrollment" class="chat-group-section">
                <!-- Loading state while checking chat membership -->
                <button
                  v-if="checkingChatStatus"
                  class="chat-group-btn chat-group-btn--loading"
                  disabled
                >
                  <i class="fas fa-spinner fa-spin"></i>
                  {{ $t("common.checking") || "جاري التحقق..." }}
                </button>
                <!-- If user is in chat group - show enter button -->
                <button
                  v-else-if="courseDetails.conversation_id"
                  class="chat-group-btn chat-group-btn--enter"
                  @click="openCourseChat"
                >
                  <i class="fas fa-comments"></i>
                  {{ $t("course.enter_chat_group") || "دخول مجموعة الدورة" }}
                </button>
                <!-- If user left/not in chat group - show join button -->
                <button
                  v-else
                  class="chat-group-btn chat-group-btn--join"
                  :disabled="joiningChatGroup"
                  @click="joinChatGroup"
                >
                  <i v-if="joiningChatGroup" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-user-plus"></i>
                  {{ joiningChatGroup ? ($t("common.loading") || "جاري...") : ($t("course.join_chat_group") || "الانضمام لمجموعة الدورة") }}
                </button>
              </div>

              <!-- Enrollment Check Loader for Button -->
              <div v-if="checkingEnrollment" class="btn-loader-wrapper">
                <div class="btn-loader">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <!-- Add to Cart Button -->
              <button
                class="enroll-btn"
                v-else-if="
                  !isEnrolled(courseDetails.course.id) &&
                  courseDetails.course.price != null &&
                  courseDetails.course.price != 0
                "
                @click="addToCart(courseDetails.course.id, $event, 'course')"
              >
                <i class="fas fa-cart-plus"></i>
                {{ $t("bundle.add_to_cart") }}
              </button>
              <!-- Free Enroll Button -->
              <button
                class="enroll-btn free-btn"
                v-else-if="
                  !isEnrolled(courseDetails.course.id) &&
                  (courseDetails.course.price === null ||
                    courseDetails.course.price === 0) &&
                  (courseDetails.course.discount_price === null ||
                    courseDetails.course.discount_price === 0)
                "
                @click="enrollFree(courseDetails.course.id)"
              >
                <i class="fas fa-graduation-cap"></i>
                {{ $t("course.enroll_free") }}
              </button>

              <!-- Create Bundle Button -->
              <button
                v-if="canCreateBundle"
                class="create-bundle-btn-sidebar"
                @click="openCustomBundleModal"
              >
                <div class="bundle-btn-content">
                  <div class="bundle-btn-icon">
                    <i class="fas fa-layer-group"></i>
                  </div>
                  <div class="bundle-btn-text">
                    <span class="bundle-btn-title">{{ $t("custom_bundle.create_bundle") || "إنشاء باقة" }}</span>
                    <span class="bundle-btn-subtitle">{{ $t("custom_bundle.save_more") || "وفّر أكثر مع الباقات" }}</span>
                  </div>
                </div>
                <i class="fas fa-chevron-left bundle-btn-arrow"></i>
              </button>
            </div>

            <!-- Instructor Card -->
            <div class="instructor-card">
              <div class="instructor-header">
                <h3>{{ $t("course.instructor") }}</h3>
              </div>
              <div class="instructor-info">
                <div class="instructor-avatar">
                  <img
                    :src="`${courseDetails.course.preview_image}`"
                    :alt="$t('common.image_alt')"
                  />
                </div>
                <div class="instructor-details">
                  <h4 class="instructor-name">
                    {{ courseDetails.course.user.fname }}
                    {{ courseDetails.course.user.lname }}
                  </h4>
                  <span class="instructor-badge">
                    <i class="fas fa-award"></i>
                    {{ $t("course.instructor_badge") }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- Course Content -->
          <div class="course-content-area">
            <!-- Tabs -->
            <div class="course-tabs">
              <ul class="nav nav-pills" role="tablist">
                <li class="nav-item">
                  <a
                    class="nav-link active"
                    data-bs-toggle="tab"
                    href="#kt_user_view_overview_tab"
                  >
                    <i class="fas fa-list-ul"></i>
                    {{ $t("course.tab_lectures") }}
                  </a>
                </li>
                <li class="nav-item">
                  <a
                    class="nav-link"
                    data-bs-toggle="tab"
                    href="#kt_overview_tab"
                  >
                    <i class="fas fa-info-circle"></i>
                    {{ $t("course.overview") }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Tab content -->
            <div class="tab-content">
              <!-- Overview Tab -->
              <div class="tab-pane fade" id="kt_overview_tab" role="tabpanel">
                <div class="overview-card">
                  <h3 class="section-title">
                    <i class="fas fa-chart-bar"></i>
                    {{ $t("course.course_stats") }}
                  </h3>
                  <div class="stats-grid">
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-play-circle"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value">{{ totalClasses }}</span>
                        <span class="stat-label">{{
                          $t("course.lectures")
                        }}</span>
                      </div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-book"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value">{{ totalChapters }}</span>
                        <span class="stat-label">{{
                          $t("course.chapters")
                        }}</span>
                      </div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-user"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value"
                          >{{ courseDetails.course.user.fname }}
                          {{ courseDetails.course.user.lname }}</span
                        >
                        <span class="stat-label">{{
                          $t("course.instructor")
                        }}</span>
                      </div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-clock"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value">{{
                          formattedDuration(courseDetails.course.duration)
                        }}</span>
                        <span class="stat-label">{{
                          $t("course.duration")
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Lectures Tab -->
              <div
                class="tab-pane fade show active"
                id="kt_user_view_overview_tab"
                role="tabpanel"
              >
                <div class="lectures-card">
                  <!-- Units List -->
                  <div
                    v-for="(unit, unitIndex) in units"
                    :key="unit.id"
                    class="unit-section"
                  >
                    <div class="unit-header">
                      <div class="unit-info">
                        <input
                          v-if="isEnrolled(courseDetails.course.id)"
                          type="checkbox"
                          class="custom-checkbox"
                          :checked="unit.is_checked"
                          @change="toggleCheck(unit, 'Unit')"
                        />
                        <div class="unit-number">{{ unitIndex + 1 }}</div>
                        <div class="unit-title-wrapper">
                          <h3 class="unit-title">{{ unit.title }}</h3>
                          <span class="unit-duration">
                            <i class="bi bi-clock"></i>
                            {{ formattedDuration(unit.duration) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Chapters Accordion for each Unit -->
                    <div class="chapters-list">
                      <div
                        v-for="(chapter, chapterIndex) in unit.chapters"
                        :key="chapter.id"
                        class="chapter-item"
                      >
                        <div
                          class="chapter-header"
                          data-bs-toggle="collapse"
                          :data-bs-target="
                            '#collapse' + chapterIndex + '-' + unit.id
                          "
                        >
                          <div class="chapter-info">
                            <input
                              v-if="isEnrolled(courseDetails.course.id)"
                              type="checkbox"
                              class="custom-checkbox"
                              :checked="chapter.is_checked"
                              @change="
                                toggleCheck(chapter, 'course_chapter', unit)
                              "
                              @click.stop
                            />
                            <div class="chapter-icon">
                              <i class="fas fa-folder"></i>
                            </div>
                            <div class="chapter-details">
                              <span class="chapter-title">{{
                                chapter.title
                              }}</span>
                              <span class="chapter-meta">
                                <i class="bi bi-clock"></i>
                                {{ formattedDuration(chapter.duration) }}
                                <span class="classes-count">
                                  <i class="fas fa-play-circle"></i>
                                  {{ chapter.classes.length }}
                                </span>
                              </span>
                            </div>
                          </div>
                          <i class="fas fa-chevron-down chapter-arrow"></i>
                        </div>

                        <div
                          :id="'collapse' + chapterIndex + '-' + unit.id"
                          :class="[
                            'collapse',
                            chapterIndex === selectedChapterIndex ? 'show' : '',
                          ]"
                        >
                          <div class="classes-list">
                            <div
                              v-for="classItem in chapter.classes"
                              :key="classItem.id"
                              class="class-item"
                            >
                              <div class="class-info">
                                <input
                                  v-if="isEnrolled(courseDetails.course.id)"
                                  type="checkbox"
                                  class="custom-checkbox"
                                  :checked="classItem.is_checked"
                                  @change="
                                    toggleCheck(
                                      classItem,
                                      'course_class',
                                      chapter
                                    )
                                  "
                                />
                                <div class="class-type-icon">
                                  <i
                                    v-if="classItem.type == 'video'"
                                    class="fas fa-play"
                                  ></i>
                                  <i
                                    v-else-if="classItem.type == 'pdf'"
                                    class="fas fa-file-pdf"
                                  ></i>
                                  <i
                                    v-else-if="classItem.type == 'link'"
                                    class="fas fa-link"
                                  ></i>
                                  <i v-else class="fas fa-file"></i>
                                </div>
                                <span class="class-title">{{
                                  classItem.title
                                }}</span>
                              </div>
                              <div class="class-actions">
                                <span class="class-duration">
                                  <i class="bi bi-clock"></i>
                                  {{ formattedDuration(classItem.duration) }}
                                </span>
                                <span
                                  v-if="
                                    classVideoUrl(classItem) != null &&
                                    isEnrolled(courseDetails.course.id) &&
                                    classItem.type == 'video' &&
                                    classItem.is_live != true
                                  "
                                  class="action-btn play-btn"
                                  @click="playClass(classItem)"
                                >
                                  <i class="fa fa-play"></i>
                                </span>
                                <span
                                  v-if="
                                    classVideoUrl(classItem) != null &&
                                    isEnrolled(courseDetails.course.id) &&
                                    classItem.type == 'link' &&
                                    classItem.is_live != true
                                  "
                                  class="action-btn link-btn"
                                  data-bs-toggle="modal"
                                  data-bs-target="#view-link-modal"
                                >
                                  <i class="fa fa-link"></i>
                                </span>
                                <span
                                  v-if="
                                    classVideoUrl(classItem) != null &&
                                    isEnrolled(courseDetails.course.id) &&
                                    classItem.is_live == true
                                  "
                                  class="action-btn live-btn"
                                  @click="playClass(classItem)"
                                >
                                  <i class="fa fa-circle"></i>
                                  {{ $t("livestream.live") }}
                                </span>
                                <span
                                  v-if="classItem.type == 'pdf'"
                                  class="action-btn pdf-btn"
                                  @click="showPdf(classItem)"
                                >
                                  <i class="fa fa-file-pdf"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <VideoClass ref="vdRef" />
  <ShareModal />
  <div
    dir="rtl"
    class="modal fade forgetpassword-modal"
    id="view-link-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="exampleModalCenterTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="exampleModalLongTitle">
            {{ $t("course.view_course_content") }}
          </h5>
        </div>
        <div class="modal-body" style="text-align: center">
          <h4 class="mb-2 text-center support-text">
            {{ $t("course.view_link_via_app") }}
          </h4>
          <div class="support-box">
            <img
              class="support-img text-center"
              width="250"
              src="assets/img/logo-dark.png"
              alt=""
            />
          </div>
          <hr />
          <div class="text-center">
            <h5 class="download-app">
              <i class="fa fa-link"></i>
              <a href="https://onelink.to/speetar.academia">{{
                $t("course.app_download_link")
              }}</a>
            </h5>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t("common.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Custom Bundle Modal -->
  <div
    class="modal fade"
    id="customBundleModal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="customBundleModalLabel"
    aria-hidden="true"
    ref="customBundleModal"
  >
    <div
      class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
      role="document"
    >
      <div class="modal-content custom-bundle-modal">
        <div class="modal-header">
          <h5 class="modal-title" id="customBundleModalLabel">
            <span v-if="bundleStep === 1">{{
              $t("category_courses.available_courses") || "الدورات المتاحة"
            }}</span>
            <span v-else>{{
              $t("category_courses.selected_courses_review") ||
              "مراجعة الدورات المختارة"
            }}</span>
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetBundleStep"
          ></button>
        </div>

        <!-- Step 1: Course Selection -->
        <div v-if="bundleStep === 1" class="modal-body">
          <!-- Loading State -->
          <div v-if="isLoadingCustomCourses" class="custom-bundle-loading">
            <div class="spinner"></div>
            <p>{{ $t("common.loading") || "جاري التحميل..." }}</p>
          </div>

          <!-- No Courses -->
          <div
            v-else-if="customCourses.length === 0"
            class="custom-bundle-empty"
          >
            <i class="fas fa-box-open"></i>
            <p>
              {{ $t("category_courses.no_courses") || "لا توجد دورات متاحة" }}
            </p>
          </div>

          <!-- Courses Grid -->
          <div v-else class="custom-bundle-grid">
            <div
              v-for="course in customCourses"
              :key="course.id"
              class="custom-bundle-card"
              :class="{ selected: selectedBundleCourses.includes(course.id) }"
            >
              <div class="bundle-card-image">
                <img
                  :src="course.image || course.preview_image || defaultImage"
                  :alt="course.title"
                  @error="setDefaultImage"
                />
                <div class="bundle-card-price">
                  {{ course.price || 0 }} {{ $appCurrency }}
                </div>
              </div>
              <div class="bundle-card-content">
                <h4 class="bundle-card-title">{{ course.title }}</h4>
                <p class="bundle-card-instructor">
                  {{ course.user?.fname }} {{ course.user?.lname }}
                </p>
              </div>
              <div class="bundle-card-actions">
                <button
                  v-if="!selectedBundleCourses.includes(course.id)"
                  class="bundle-add-btn"
                  @click="addToBundleSelection(course.id)"
                >
                  <i class="fas fa-plus"></i>
                  {{ $t("category_courses.add_to_bundle") || "إضافة للباقة" }}
                </button>
                <button
                  v-else
                  class="bundle-remove-btn"
                  @click="removeFromBundleSelection(course.id)"
                >
                  <i class="fas fa-minus"></i>
                  {{
                    $t("category_courses.remove_from_bundle") ||
                    "إزالة من الباقة"
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div
            v-if="
              customCourseLinks &&
              customCourseLinks.links &&
              customCourseLinks.links.length > 3
            "
            class="custom-bundle-pagination"
          >
            <button
              class="pagination-btn"
              :disabled="!customCourseLinks.links[0].url"
              @click="fetchCustomBundleCourses(customCourseLinks.links[0].page)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="pagination-info">
              {{ $t("common.page") || "صفحة" }} {{ currentCustomPage }}
            </span>
            <button
              class="pagination-btn"
              :disabled="
                !customCourseLinks.links[customCourseLinks.links.length - 1].url
              "
              @click="
                fetchCustomBundleCourses(
                  customCourseLinks.links[customCourseLinks.links.length - 1]
                    .page
                )
              "
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>

        <!-- Step 1 Footer: Show Courses & Continue -->
        <div
          v-if="bundleStep === 1 && customCourses.length > 0"
          class="modal-footer custom-bundle-footer"
        >
          <div class="bundle-summary">
            <div class="summary-row">
              <span
                >{{
                  $t("category_courses.selected_courses") || "الدورات المختارة"
                }}:</span
              >
              <span class="summary-value">{{
                selectedBundleCourses.length
              }}</span>
            </div>
            <div class="summary-row total">
              <span
                >{{
                  $t("category_courses.base_price") || "السعر الأساسي"
                }}:</span
              >
              <span class="summary-value"
                >{{ bundleBasePrice }} {{ $appCurrency }}</span
              >
            </div>
          </div>
          <button
            class="bundle-continue-btn"
            :disabled="selectedBundleCourses.length < bundleMinCourses"
            @click="goToStep2"
          >
            <i class="fas fa-eye"></i>
            {{
              $t("category_courses.show_courses_continue") ||
              "عرض الدورات والمتابعة"
            }}
          </button>
          <p
            v-if="selectedBundleCourses.length < bundleMinCourses"
            class="min-courses-hint"
          >
            {{
              $t("category_courses.min_courses_hint", {
                min: bundleMinCourses,
              }) || `يجب اختيار ${bundleMinCourses} دورات على الأقل`
            }}
          </p>
        </div>

        <!-- Step 2: Review Selected Courses -->
        <div v-if="bundleStep === 2" class="modal-body bundle-review-body">
          <div class="bundle-review-header">
            <button class="back-btn" @click="goToStep1">
              <i class="fas fa-arrow-right"></i>
              {{ $t("common.back") || "رجوع" }}
            </button>
          </div>

          <!-- Selected Courses List -->
          <div class="selected-courses-list">
            <div
              v-for="course in selectedCoursesDetails"
              :key="course.id"
              class="selected-course-item"
            >
              <div class="selected-course-image">
                <img
                  :src="course.preview_image || defaultImage"
                  :alt="course.title"
                  @error="setDefaultImage"
                />
              </div>
              <div class="selected-course-info">
                <h4 class="selected-course-title">{{ course.title }}</h4>
                <p class="selected-course-instructor">
                  {{ course.user?.fname }} {{ course.user?.lname }}
                </p>
              </div>
              <div class="selected-course-price">
                {{ course.price || 0 }} {{ $appCurrency }}
              </div>
              <button
                class="remove-course-btn"
                @click="removeFromBundleSelection(course.id)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="bundle-order-summary">
            <h4 class="order-summary-title">
              <i class="fas fa-receipt"></i>
              {{ $t("custom_bundle.order_details") || "تفاصيل الطلب" }}
            </h4>
            <div class="order-summary-rows">
              <div class="order-row">
                <span>{{
                  $t("category_courses.selected_courses") || "الدورات المختارة"
                }}</span>
                <span>{{ selectedBundleCourses.length }}</span>
              </div>
              <div class="order-row">
                <span>{{
                  $t("category_courses.base_price") || "السعر الأساسي"
                }}</span>
                <span>{{ bundleBasePrice }} {{ $appCurrency }}</span>
              </div>
              <div class="order-row discount">
                <span>{{ $t("category_courses.discount") || "الخصم" }}</span>
                <span>{{ bundleDiscountPercent }}%</span>
              </div>
              <div class="order-row total">
                <span>{{
                  $t("category_courses.final_price") || "السعر النهائي"
                }}</span>
                <span
                  >{{ bundleFinalPrice.toFixed(2) }} {{ $appCurrency }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2 Footer: Buy Now -->
        <div v-if="bundleStep === 2" class="modal-footer custom-bundle-footer">
          <button
            class="bundle-enroll-btn"
            :disabled="
              selectedBundleCourses.length < bundleMinCourses ||
              isEnrollingBundle
            "
            @click="confirmBundleEnroll"
          >
            <span v-if="isEnrollingBundle">
              <i class="fas fa-spinner fa-spin"></i>
              {{ $t("common.loading") || "جاري التحميل..." }}
            </span>
            <span v-else>
              <i class="fas fa-shopping-cart"></i>
              {{ $t("category_courses.buy_now") || "اشتري الآن" }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { baseUrl, apiUrl, api, drmPlayerUrl } from "@/utils/api"; // Adjust the import path according to your project structure
import VideoClass from "./video.vue";
import ShareModal from "./share.vue";
import Swal from "sweetalert2";
import { ChatService } from "@/services/chat";

import {
  showCart,
  addToCart,
  confirmPurchase,
  applyCoupon,
  removeCartItem,
  removeCoupon,
  generateCartInvoice,
} from "@/utils/cart";
export default {
  name: "CourseDetails",
  components: {
    VideoClass,
    ShareModal,
  },
  data() {
    return {
      token: localStorage.getItem("access_token"),
      baseUrl: baseUrl, // Adjust as per your .env configuration
      courseId: this.$route.query.id,
      defaultImage: "/assets/img/acad-icon.png",
      enrolledCourses: [], // Store enrolled courses here
      courseDetails: {
        course: {
          category: {},
          user: {},
          institute: null,
          tele_group: "",
        }, // Initialize with an empty object
      },
      courseclass: 0,
      units: [], // To store units data
      selectedUnitIndex: 0, // Index of the selected unit
      loading: true, // Add loading state
      checkingEnrollment: true, // Add enrollment check loading state
      // Custom Bundle Data
      customCourses: [],
      customCoursesCache: [],
      selectedBundleCourses: [],
      bundleDiscounts: [],
      bundleMinCourses: 2,
      bundleMaxCourses: 5,
      isCustomizable: false,
      isLoadingCustomCourses: false,
      isEnrollingBundle: false,
      customCourseLinks: null,
      currentCustomPage: 1,
      bundleStep: 1, // Step 1: Select courses, Step 2: Review & Buy
      joiningChatGroup: false, // Loading state for joining chat group
      checkingChatStatus: false, // Loading state for checking chat group membership
      courseSubCategoryId: null, // Sub category ID from /api/courses/{course}
    };
  },
  computed: {
    totalChapters() {
      return this.courseDetails.course.units.reduce((acc, unit) => {
        return acc + unit.chapters.length;
      }, 0);
      return 0;
    },
    totalClasses() {
      let total = 0;
      this.courseDetails.course.units.forEach((unit) => {
        unit.chapters.forEach((chapter) => {
          total += chapter.classes.length;
        });
      });
      return total;
    },
    // Custom Bundle Computed Properties
    bundleBasePrice() {
      return this.selectedBundleCourses.reduce((total, courseId) => {
        const course = this.customCoursesCache.find((c) => c.id === courseId);
        return total + (parseFloat(course?.price) || 0);
      }, 0);
    },
    bundleDiscountPercent() {
      const discount = this.bundleDiscounts.find(
        (d) => d.courses_count === this.selectedBundleCourses.length
      );
      return discount?.discount || 0;
    },
    bundleFinalPrice() {
      const discount = this.bundleDiscountPercent;
      return (this.bundleBasePrice * (100 - discount)) / 100;
    },
    canCreateBundle() {
      // Show "Create Bundle" button only when user is NOT enrolled in the course
      if (this.checkingEnrollment) return false;
      if (!this.courseDetails || !this.courseDetails.course) return false;
      return !this.isEnrolled(this.courseDetails.course.id);
    },
    selectedCoursesDetails() {
      return this.selectedBundleCourses
        .map((courseId) => {
          return this.customCoursesCache.find((c) => c.id === courseId);
        })
        .filter((c) => c);
    },
  },
  async mounted() {
    // Fetch course details and enrolled courses in parallel
    await Promise.all([
      this.fetchCourseDetails(),
      this.myCourses()
    ]);
    
    // Check for vc parameter in URL after course details are loaded
    const urlParams = new URLSearchParams(window.location.search);
    const videoId = urlParams.get("vc");
    if (videoId) {
      this.playVideoById(videoId);
    }
    
    // Check chat group membership status after both are loaded
    this.checkChatGroupStatus();

    // Academia DRM player: receive currentTime updates via postMessage
    // so the notes feature keeps working (VdoCipher path polls instead).
    this.drmMessageHandler = (event) => {
      if (event.origin !== drmPlayerUrl) return;
      const currentTime = event.data && event.data.currentTime;
      if (typeof currentTime === "number") {
        const input = document.getElementById("videoCurrentTime");
        if (input) input.value = currentTime;
      }
    };
    window.addEventListener("message", this.drmMessageHandler);
  },
  unmounted() {
    window.removeEventListener("message", this.drmMessageHandler);
  },
  methods: {
    async toggleCheckB(item, type, parent = null) {
      const isChecked = !item.is_checked; // الحالة الجديدة

      // ✅ تحديث فوري في الواجهة قبل Swal و قبل الباك اند
      item.is_checked = isChecked;

      if (type === "Unit") {
        item.chapters.forEach((ch) => {
          ch.is_checked = isChecked;
          ch.classes.forEach((cl) => (cl.is_checked = isChecked));
        });
      }

      if (type === "course_chapter") {
        item.classes.forEach((cl) => (cl.is_checked = isChecked));
      }

      Swal.fire({
        title: isChecked ? "تأكيد التحديد" : "تأكيد إلغاء التحديد",
        text: "هل أنت متأكد من هذه العملية؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "نعم",
        cancelButtonText: "إلغاء",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const endpoint = isChecked
              ? `${apiUrl}/course/progress/check`
              : `${apiUrl}/course/progress/uncheck`;

            await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-country-code": window.appRegion.phone_code,
                Authorization: `Bearer ${this.token}`,
              },
              body: JSON.stringify({
                model_id: item.id,
                model_type: type,
              }),
            });
          } catch (error) {
            console.error(error);
          }
        } else {
          // ❌ لو المستخدم لغى → رجّع الوضع القديم
          item.is_checked = !isChecked;

          if (type === "Unit") {
            item.chapters.forEach((ch) => {
              ch.is_checked = item.is_checked;
              ch.classes.forEach((cl) => (cl.is_checked = item.is_checked));
            });
          }

          if (type === "course_chapter") {
            item.classes.forEach((cl) => (cl.is_checked = item.is_checked));
          }
        }
      });
    },
    async toggleCheck(item, type, parent = null) {
      const isChecked = !item.is_checked; // الحالة الجديدة

      // ✅ تحديث فوري في الواجهة
      item.is_checked = isChecked;

      if (type === "Unit") {
        item.chapters.forEach((ch) => {
          ch.is_checked = isChecked;
          ch.classes.forEach((cl) => (cl.is_checked = isChecked));
        });
      }

      if (type === "course_chapter") {
        item.classes.forEach((cl) => (cl.is_checked = isChecked));
      }

      try {
        const endpoint = isChecked
          ? `${apiUrl}/course/progress/check`
          : `${apiUrl}/course/progress/uncheck`;

        await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            model_id: item.id,
            model_type: type,
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },

    async enrollFree(courseId) {
      Swal.fire({
        title: this.$t("course.confirm_enroll"),
        text: this.$t("common.confirm_action"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("common.yes"),
        cancelButtonText: this.$t("common.cancel"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const endpoint = `${apiUrl}/free/enroll`;

            const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-country-code": window.appRegion.phone_code,
                Authorization: `Bearer ${this.token}`,
              },
              body: JSON.stringify({
                course_id: courseId,
              }),
            });

            const data = await response.json();

            if (data.status === "success") {
              Swal.fire({
                icon: "success",
                title: this.$t("course.enroll_success_title"),
                text: data.message || this.$t("course.enroll_success_text"),
                confirmButtonText: this.$t("common.ok"),
              }).then(() => {
                window.location.reload(); // إعادة تحميل الصفحة
              });
            } else {
              Swal.fire({
                icon: "error",
                title: this.$t("common.error"),
                text: data.message || this.$t("course.enroll_error"),
              });
            }
          } catch (error) {
            console.error(error);
            Swal.fire({
              icon: "error",
              title: this.$t("common.error"),
              text: this.$t("common.server_unreachable"),
            });
          }
        }
      });
    },

    formattedDuration(durationInSeconds) {
      const totalSeconds = Number(durationInSeconds);

      if (isNaN(totalSeconds)) {
        return "Invalid duration";
      }

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const formattedHours = String(hours).padStart(2, "0");
      const formattedMinutes = String(minutes).padStart(2, "0");
      const formattedSeconds = String(seconds).padStart(2, "0");

      return `
        ${formattedHours} : ${formattedMinutes} : ${formattedSeconds}`;
    },
    calculateDiscountPercentage(price, discountPrice) {
      if (price > 0) {
        return Math.round(((price - discountPrice) / price) * 100);
      }
      return 0;
    },
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },

    // Custom Bundle Methods
    async openCustomBundleModal() {
      // Reset to step 1 when opening modal
      this.bundleStep = 1;
      // Pre-select the current course when opening the modal
      const currentCourseId = this.courseDetails.course.id;
      if (
        currentCourseId &&
        !this.selectedBundleCourses.includes(currentCourseId)
      ) {
        this.selectedBundleCourses = [currentCourseId];
        // Add current course to cache for price calculation
        if (!this.customCoursesCache.some((c) => c.id === currentCourseId)) {
          this.customCoursesCache.push(this.courseDetails.course);
        }
      }
      
      // Open the modal first
      const modalElement = document.getElementById('customBundleModal');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
      
      // Fetch sub_category_id from course API if not already fetched
      if (!this.courseSubCategoryId) {
        await this.fetchCourseSubCategoryId();
      }
      // Always fetch customizable courses when opening modal
      if (this.courseSubCategoryId) {
        this.fetchCustomBundleCourses();
      }
    },
    async fetchCourseSubCategoryId() {
      try {
        const response = await fetch(`${apiUrl}/courses/${this.courseId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch course info");

        const result = await response.json();
        
        // Extract subcategory_id from result.data.subcategory_id
        if (result.data?.subcategory_id) {
          this.courseSubCategoryId = result.data.subcategory_id;
        }
        
        console.log("Extracted sub_category_id:", this.courseSubCategoryId);
      } catch (error) {
        console.error("Error fetching course sub_category_id:", error);
      }
    },
    goToStep2() {
      if (this.selectedBundleCourses.length >= this.bundleMinCourses) {
        this.bundleStep = 2;
      }
    },
    goToStep1() {
      this.bundleStep = 1;
    },
    resetBundleStep() {
      this.bundleStep = 1;
    },
    async fetchCustomBundleCourses(page = 1) {
      if (!this.courseSubCategoryId) return;
      this.isLoadingCustomCourses = true;

      try {
        const data = { 
          page,
          sub_category_id: this.courseSubCategoryId 
        };

        const response = await fetch(`${apiUrl}/web/get-customizable-courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) throw new Error("Failed to fetch");

        const result = await response.json();
        console.log("Customizable courses response:", result);

        if (result.courses?.data) {
          this.customCourses = result.courses.data;
          console.log("Custom courses:", this.customCourses);
          // Cache courses for price calculation
          result.courses.data.forEach((course) => {
            if (!this.customCoursesCache.some((c) => c.id === course.id)) {
              this.customCoursesCache.push(course);
            }
          });
          this.customCourseLinks = result.courses.meta;
          this.currentCustomPage = result.courses.meta?.current_page || 1;
        }

        if (result.discounts) {
          this.bundleDiscounts = result.discounts;
        }

        if (result.sub_category) {
          this.bundleMinCourses =
            parseInt(result.sub_category.min_courses) || 2;
          this.bundleMaxCourses =
            parseInt(result.sub_category.max_courses) || 5;
          this.isCustomizable = result.sub_category.customizable === "1";
        }
      } catch (error) {
        console.error("Error fetching custom bundle courses:", error);
        this.customCourses = [];
      } finally {
        this.isLoadingCustomCourses = false;
      }
    },
    addToBundleSelection(courseId) {
      if (this.selectedBundleCourses.includes(courseId)) return;
      if (this.selectedBundleCourses.length >= this.bundleMaxCourses) {
        Swal.fire({
          icon: "warning",
          title:
            this.$t("category_courses.max_courses_alert", {
              max: this.bundleMaxCourses,
            }) || `الحد الأقصى ${this.bundleMaxCourses} دورات`,
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        });
        return;
      }
      this.selectedBundleCourses.push(courseId);
    },
    removeFromBundleSelection(courseId) {
      this.selectedBundleCourses = this.selectedBundleCourses.filter(
        (id) => id !== courseId
      );
      // If in step 2 and courses fall below minimum, go back to step 1
      if (
        this.bundleStep === 2 &&
        this.selectedBundleCourses.length < this.bundleMinCourses
      ) {
        this.bundleStep = 1;
      }
    },
    async confirmBundleEnroll() {
      if (this.selectedBundleCourses.length < this.bundleMinCourses) {
        Swal.fire({
          icon: "warning",
          title:
            this.$t("category_courses.min_courses_alert", {
              min: this.bundleMinCourses,
            }) || `يجب اختيار ${this.bundleMinCourses} دورات على الأقل`,
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        });
        return;
      }

      const result = await Swal.fire({
        title:
          this.$t("category_courses.confirm_enroll") ||
          "هل أنت متأكد من الاشتراك؟",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("common.yes") || "نعم",
        cancelButtonText: this.$t("common.cancel") || "إلغاء",
        confirmButtonColor: "#00c744",
      });

      if (result.isConfirmed) {
        this.enrollInBundle();
      }
    },
    async enrollInBundle() {
      this.isEnrollingBundle = true;

      try {
        const enrollmentData = {
          courses_ids: this.selectedBundleCourses,
        };

        // Use sub_category_id fetched from course API
        if (this.courseSubCategoryId) {
          enrollmentData.subcategory_id = this.courseSubCategoryId;
        }

        const response = await fetch(`${apiUrl}/custom-bundles/enroll`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(enrollmentData),
        });

        if (!response.ok) throw new Error("Failed to enroll");

        const result = await response.json();

        Swal.fire({
          icon: "success",
          title:
            this.$t("category_courses.enroll_success") || "تم الاشتراك بنجاح",
          text:
            this.$t("category_courses.enroll_success_text") ||
            "يمكنك الآن الوصول إلى جميع الدورات",
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        }).then(() => {
          // Close modal and redirect to My Courses
          const modal = bootstrap.Modal.getInstance(
            this.$refs.customBundleModal
          );
          if (modal) modal.hide();
          this.selectedBundleCourses = [];
          window.location.href = "/my-courses"; // Redirect to My Courses page
        });
      } catch (error) {
        console.error("Error enrolling in bundle:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error") || "خطأ",
          text: this.$t("category_courses.enroll_error") || "فشل في الاشتراك",
          confirmButtonText: this.$t("common.ok") || "موافق",
        });
      } finally {
        this.isEnrollingBundle = false;
      }
    },
    async fetchCourseDetails() {
      const data = {
        course_id: this.courseId,
        get_duration: 1, // Assuming you need to fetch duration as well
      };

      try {
        const response = await fetch(`${apiUrl}/desktop/course-details`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error(this.$t("course.failed_to_fetch_course_details"));
        }

        const responseData = await response.json();
        this.courseDetails = responseData; // Store all course details
        //this.courseclass = responseData.course.courseclass.length; // Store all course details
        let shareModal = document.getElementById("share-content");

        const t = this.$t ? this.$t.bind(this) : (k) => k;

        // Decode Unicode escape sequences in course title
        const decodedTitle = responseData.course.title.replace(
          /\\u([0-9a-fA-F]{4})/g,
          (match, grp) => String.fromCharCode(parseInt(grp, 16))
        );

        shareModal.innerHTML = `
          <div style="text-align:right">
              ${t("share.app_name")}📚📱<br><br>
              ${decodedTitle}<br><br>
              <a href="https://instructor.academia.ly/course/?id=${
                responseData.course.id
              }" target="_blank">https://instructor.academia.ly/course/?id=${
          responseData.course.id
        }</a><br><br>
              ${t("share.contact_info")}
          </div>
          <div id="copyButton" class="mt-3 btn-sm btn btn-success">${t(
            "share.copy_button"
          )} <i class="fa fa-copy"></i></div>
      `;

        document
          .getElementById("copyButton")
          .addEventListener("click", async function () {
            // Define the text to be copied
            const textToCopy = `${t(
              "share.app_name"
            )}📚📱\n\n${decodedTitle}\n\nhttps://instructor.academia.ly/course/?id=${
              responseData.course.id
            }\n\n${t("share.contact_info")}`;

            try {
              // Use the Clipboard API to copy the text
              await navigator.clipboard.writeText(textToCopy);
              // Optionally, show a confirmation message
              Swal.fire({
                icon: "success",
                title: t("share.copied_title"),
                text: "",
                confirmButtonText: t("common.ok"),
              });
            } catch (err) {
              console.error(t("share.copy_failed_log"), err);
              alert(t("share.copy_failed_alert"));
            }
          });
        let shareWebModal = document.getElementById("share-web-content");
        const dynamicBaseUrl = window.location.origin;

        shareWebModal.innerHTML = `
                <div style="text-align:right">
                    ${t("share.app_name")}📚📱<br><br>
                    ${decodedTitle}<br><br>
                    <a href="${dynamicBaseUrl}/course-details?id=${
          responseData.course.id
        }" target="_blank">${dynamicBaseUrl}/course-details?id=${
          responseData.course.id
        }</a><br><br>
                    ${t("share.contact_info")}
                </div>
                <div id="copyButtonWeb" class="mt-3 btn-sm btn btn-success">${t(
                  "share.copy_button"
                )} <i class="fa fa-copy"></i></div>
              `;

        document
          .getElementById("copyButtonWeb")
          .addEventListener("click", async function () {
            // Define the text to be copied
            const textToCopy = `${t(
              "share.app_name"
            )}📚📱\n\n${decodedTitle}\n\n${dynamicBaseUrl}/course-details?id=${
              responseData.course.id
            }\n\n${t("share.contact_info")}`;

            try {
              // Use the Clipboard API to copy the text
              await navigator.clipboard.writeText(textToCopy);
              // Optionally, show a confirmation message
              Swal.fire({
                icon: "success",
                title: t("share.copied_title"),
                text: "",
                confirmButtonText: t("common.ok"),
              });
            } catch (err) {
              console.error(t("share.copy_failed_log"), err);
              alert(t("share.copy_failed_alert"));
            }
          });
        if (responseData.course.units && responseData.course.units.length > 0) {
          this.units = responseData.course.units; // Store units data
          console.log(responseData.course.units);
        }
        // Set isCustomizable from category or sub_category data
        if (
          responseData.course.category?.customizable === "1" ||
          responseData.course.category?.customizable === 1 ||
          responseData.sub_category?.customizable === "1"
        ) {
          this.isCustomizable = true;
        }
      } catch (error) {
        console.error(this.$t("course.error_fetching_course_details"), error);
        console.error("Error fetching course details:", error);
        // Handle error as per your application's requirements
      } finally {
        this.loading = false; // Set loading to false after fetching data
      }
    },
    isEnrolled(courseId) {
      return this.enrolledCourses.some((course) => course.id === courseId);
    },
    // Open course chat group
    openCourseChat() {
      if (this.courseDetails.conversation_id) {
        this.$router.push({
          name: "ChatConversation",
          params: { conversationId: this.courseDetails.conversation_id },
        });
      }
    },
    // Check if user is already in the course chat group
    async checkChatGroupStatus() {
      // Only check if user is enrolled and conversation_id is not already set
      const isEnrolled = this.isEnrolled(this.courseDetails?.course?.id);
      console.log('[ChatGroup] Checking status - isEnrolled:', isEnrolled, 'courseId:', this.courseId);
      
      if (!isEnrolled) {
        console.log('[ChatGroup] User not enrolled, skipping check');
        return;
      }
      if (this.courseDetails?.conversation_id) {
        console.log('[ChatGroup] Already has conversation_id:', this.courseDetails.conversation_id);
        return;
      }
      
      this.checkingChatStatus = true;
      try {
        const courseId = String(this.courseId);
        console.log('[ChatGroup] Looking for course_' + courseId);
        
        // Helper function to find course conversation
        const findCourseConversation = (conversations) => {
          return conversations.find(conv => {
            const custom = conv.custom || {};
            const match = (
              conv.id === `course_${courseId}` ||
              String(custom.courseId) === courseId ||
              String(custom.course_id) === courseId
            );
            if (match) console.log('[ChatGroup] Found match:', conv.id);
            return match;
          });
        };
        
        // First, check active conversations
        const conversations = await ChatService.getConversations();
        console.log('[ChatGroup] Available conversations:', conversations.map(c => ({ id: c.id, courseId: c.custom?.courseId })));
        
        let courseConversation = findCourseConversation(conversations);

        // If not found in active conversations, check all conversations (including deleted)
        if (!courseConversation) {
          console.log('[ChatGroup] Not found in active conversations, checking deleted...');
          const allConversations = await ChatService.getAllConversations();
          console.log('[ChatGroup] All conversations (including deleted):', allConversations.map(c => ({ id: c.id, courseId: c.custom?.courseId })));
          courseConversation = findCourseConversation(allConversations);
        }

        if (courseConversation) {
          console.log('[ChatGroup] Setting conversation_id:', courseConversation.id);
          this.courseDetails.conversation_id = courseConversation.id;
        } else {
          console.log('[ChatGroup] No matching conversation found');
        }
      } catch (error) {
        console.error("Error checking chat group status:", error);
      } finally {
        this.checkingChatStatus = false;
      }
    },
    // Join or rejoin course chat group
    async joinChatGroup() {
      if (this.joiningChatGroup) return;
      
      this.joiningChatGroup = true;
      try {
        const response = await fetch(`${apiUrl}/chat/courses/${this.courseId}/join`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
            Accept: "application/json",
          },
        });

        const result = await response.json();
        const data = result.data || result;

        if (response.ok && (result.success || data.conversation_id)) {
          // Update conversation_id in courseDetails
          this.courseDetails.conversation_id = data.conversation_id || result.conversation_id;
          
          Swal.fire({
            icon: "success",
            title: this.$t("course.joined_chat_group") || "تم الانضمام",
            text: data.message || this.$t("course.joined_chat_group_success") || "تم الانضمام لمجموعة الدورة بنجاح",
            confirmButtonText: this.$t("common.ok") || "موافق",
            confirmButtonColor: "#00c744",
          });
        } else {
          // Handle error responses
          let errorMessage = result.message || data.message || this.$t("course.join_chat_failed") || "فشل الانضمام للمجموعة";
          
          Swal.fire({
            icon: "error",
            title: this.$t("common.error") || "خطأ",
            text: errorMessage,
            confirmButtonText: this.$t("common.ok") || "موافق",
          });
        }
      } catch (error) {
        console.error("Error joining chat group:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error") || "خطأ",
          text: this.$t("course.join_chat_failed") || "فشل الانضمام للمجموعة",
          confirmButtonText: this.$t("common.ok") || "موافق",
        });
      } finally {
        this.joiningChatGroup = false;
      }
    },
    // `drm_service` is set per class; fall back to the course-level value for
    // payloads that only carry it there.
    classDrmService(course_class) {
      return (
        course_class?.drm_service ?? this.courseDetails?.course?.drm_service
      );
    },
    isAcademiaDrm(course_class) {
      return this.classDrmService(course_class) === "ACADEMIA_DRM";
    },
    // Academia DRM classes are identified by `drm_service_url`, not by the
    // VdoCipher `vc` id, so that is what the playback endpoints expect.
    classVideoUrl(course_class) {
      if (this.isAcademiaDrm(course_class)) {
        return (
          course_class.drm_service_url ??
          this.courseDetails?.course?.drm_service_url ??
          course_class.vc
        );
      }
      return course_class.vc;
    },
    playClass(course_class) {
      let isLive = course_class.is_live;
      let videoSection = document.getElementById("video-section");
      let videoLoader = document.getElementById("video-loader");
      videoSection.innerHTML = "";
      videoLoader.innerHTML = `<div class="video-loader-container">
          <div class="video-loader-content">
            <div class="video-loader-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="play-icon"><i class="fas fa-play"></i></div>
            </div>
            <p class="video-loader-text">${this.$t("common.loading")}</p>
          </div>
        </div>`;
      var myModal = new bootstrap.Modal(document.getElementById("video-modal"));
      myModal.show();
      const videoModal = document.getElementById("video-modal");
      videoModal.addEventListener("hidden.bs.modal", () => {
        videoSection.innerHTML = "";
      });
      document.getElementById("ClassId").value = course_class.id;
      this.$refs.vdRef.showClassNotes();
      if (isLive == false) {
        if (this.isAcademiaDrm(course_class)) {
          this.playAcademiaDrm(course_class, videoSection, videoLoader);
        } else {
          this.playVdoCipher(course_class, videoSection, videoLoader);
        }
      } else {
        videoLoader.innerHTML = "";

        const currentTime = new Date().getTime();
        const liveStartTime = new Date(course_class.live_starts_at).getTime();
        const liveEndTime = new Date(course_class.live_ends_at).getTime();

        let statusHTML = "";

        if (currentTime < liveStartTime) {
          // البث لم يبدأ بعد
          statusHTML = `<p class="text-success" style="#00bc2c !important"><i class="fa fa-clock"></i> ${this.$t(
            "livestream.will_start_at"
          )} ${course_class.live_starts_at}</p>`;
          //statusHTML = `<button id="joinLiveBtn" class="btn btn-dark mt-3 btn-sm"><i class="fa fa-arrow-right"></i> انضم الآن</button>`;
        } else if (currentTime >= liveStartTime && currentTime <= liveEndTime) {
          // البث جاري الآن
          statusHTML = `<button id="joinLiveBtn" class="btn btn-dark mt-3 btn-sm"><i class="fa fa-arrow-right"></i> ${this.$t(
            "livestream.join_now"
          )}</button>`;
        } else {
          // البث انتهى
          statusHTML = `<p class="text-danger"><i class="fa fa-times-circle text-danger"></i> ${this.$t(
            "livestream.ended"
          )}</p>`;
          statusHTML = `<button id="joinLiveBtn" class="btn btn-dark mt-3 btn-sm"><i class="fa fa-arrow-right"></i> ${this.$t(
            "livestream.join_now"
          )}</button>`;
        }

        videoSection.innerHTML = `
        <div class="stream-box m-2 text-center">
          <h2><i class="fa fa-circle text-danger" aria-hidden="true"></i>

             ${course_class.title}
          </h2>
          <table class="stream-table">
            <tr>
              <th>${this.$t("livestream.start_time")}</th>
              <td>${course_class.live_starts_at}</td>
            </tr>
            <tr>
              <th>${this.$t("livestream.end_time")}</th>
              <td>${course_class.live_ends_at}</td>
            </tr>
          </table>
          <div class="m-3" style="font-weight:800;font-size:medium">
            ${statusHTML}
          </div>

        </div>
      `;
        // التأكد من أن الزر موجود ثم استدعاء `joinLiveStream` عند الضغط عليه
        const joinLiveBtn = document.getElementById("joinLiveBtn");
        if (joinLiveBtn) {
          joinLiveBtn.addEventListener("click", () => {
            const button = joinLiveBtn;
            const originalHTML = button.innerHTML; // حفظ النص الأصلي للزر
            button.innerHTML = `<span class="spinner-border spinner-border-sm"></span> ${this.$t(
              "livestream.joining"
            )}`;
            button.disabled = true; // تعطيل الزر أثناء التحميل
            this.joinLiveStream(course_class); // استدعاء الفنكشن من `methods`
            setTimeout(() => {
              button.innerHTML = originalHTML; // إعادة النص الأصلي للزر
              button.disabled = false; // إعادة تفعيل الزر
            }, 3000);
          });
        }
      }
    },
    playVdoCipher(course_class, videoSection, videoLoader) {
      api(
        "/video-cipher-metadata",
        "POST",
        { url: this.classVideoUrl(course_class), class_id: course_class.id },
        { Authorization: `Bearer ${this.token}` },
        false
      ).then(async (response) => {
        videoLoader.innerHTML = " ";
        let res = await response.json();

        const isChrome =
          /Chrome/.test(navigator.userAgent) &&
          /Google Inc/.test(navigator.vendor);

        const speedOptions = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0];
        const speedOptionsHtml = speedOptions
          .map((speed) => `<option value="${speed}">${speed}x</option>`)
          .join("");

        let speedControlHtml = "";
        if (isChrome) {
          speedControlHtml = `
            <div class="speed-control">
              <div class="speed-icon">
                <i class="fas fa-tachometer-alt"></i>
              </div>
              <label class="speed-label" for="speedSelect">${this.$t(
                "video.playback_speed"
              )}</label>
              <div class="speed-select-wrapper">
                <select class="speed-select" id="speedSelect" onchange="changeSpeed()">
                  ${speedOptionsHtml}
                </select>
                <i class="fas fa-chevron-down speed-arrow"></i>
              </div>
            </div>
          `;
        }

        videoSection.innerHTML += `
            <div class="video-player-card">
              <div class="video-player-header">
                <div class="video-title-wrapper">
                  <i class="fas fa-play-circle"></i>
                  <h4 class="video-class-title">${course_class.title}</h4>
                </div>
                ${speedControlHtml}
              </div>
              <div class="video-player-body">
                <iframe
                  id="vdoCipherPlayer"
                  src="https://player.vdocipher.com/v2/?otp=${res.otp}&playbackInfo=${res.playbackInfo}&primaryColor=00c544"
                  class="video-iframe"
                  allow="encrypted-media"
                  allowfullscreen
                ></iframe>
              </div>
            </div>
          `;
        if (isChrome) {
          window.changeSpeed = function () {
            const speedSelect = document.getElementById("speedSelect");
            const selectedSpeed = speedSelect.value;
            const playerIframe = document.getElementById("vdoCipherPlayer");
            const player = VdoPlayer.getInstance(playerIframe);
            if ("playbackRate" in player.video) {
              player.video.playbackRate = parseFloat(selectedSpeed);
            } else {
              console.error("Playback rate not supported by the player.");
            }
          };
        }
        const playerIframe = document.getElementById("vdoCipherPlayer");
        const player = VdoPlayer.getInstance(playerIframe);

        setInterval(() => {
          if (
            player &&
            player.video &&
            typeof player.video.currentTime !== "undefined"
          ) {
            document.getElementById("videoCurrentTime").value =
              player.video.currentTime;
          }
        }, 5000);
      });
    },
    playAcademiaDrm(course_class, videoSection, videoLoader) {
      api(
        "/video-cipher-watermark",
        "POST",
        { url: this.classVideoUrl(course_class), class_id: course_class.id },
        { Authorization: `Bearer ${this.token}`, Accept: "application/json" },
        false
      )
        .then(async (response) => {
          videoLoader.innerHTML = " ";

          if (response.status === 401) {
            videoSection.innerHTML = `<div class="text-center text-danger m-5" style="font-weight:700">انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى</div>`;
            return;
          }
          if (!response.ok) {
            videoSection.innerHTML = `<div class="text-center text-danger m-5" style="font-weight:700">حدث خطأ أثناء تحميل الفيديو، يرجى المحاولة مرة أخرى</div>`;
            return;
          }

          let res = await response.json();
          if (!res.otp || !res.playbackInfo) {
            videoSection.innerHTML = `<div class="text-center text-danger m-5" style="font-weight:700">حدث خطأ أثناء تحميل الفيديو، يرجى المحاولة مرة أخرى</div>`;
            return;
          }

          const playerUrl = `${drmPlayerUrl}/player/${encodeURIComponent(
            res.otp
          )}/${encodeURIComponent(res.playbackInfo)}`;

          videoSection.innerHTML += `
              <div class="video-player-card">
                <div class="video-player-header">
                  <div class="video-title-wrapper">
                    <i class="fas fa-play-circle"></i>
                    <h4 class="video-class-title">${course_class.title}</h4>
                  </div>
                </div>
                <div class="video-player-body">
                  <iframe
                    id="academiaDrmPlayer"
                    src="${playerUrl}"
                    class="video-iframe"
                    allow="encrypted-media"
                    allowfullscreen
                  ></iframe>
                </div>
              </div>
            `;
        })
        .catch(() => {
          videoLoader.innerHTML = " ";
          videoSection.innerHTML = `<div class="text-center text-danger m-5" style="font-weight:700">حدث خطأ أثناء تحميل الفيديو، يرجى المحاولة مرة أخرى</div>`;
        });
    },
    showPdf(course_class) {
      const pdfUrl = `${course_class.pdf}`;

      window.open(pdfUrl, "_blank"); // Opens the PDF in a new tab
    },
    showCart,
    addToCart,
    removeCartItem,
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
    async myCourses() {
      this.loadingCourses = true; // Start loading
      this.checkingEnrollment = true; // Start enrollment check
      try {
        const response = await fetch(`${apiUrl}/desktop/my-courses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.enrolledCourses = data.courses; // Store the enrolled courses
        } else if (response.status === 401 || response.status === 403) {
          //this.$router.push("/logout");
        } else {
          console.error("Failed to fetch student details");
        }
      } catch (error) {
        console.error("Error fetching student details:", error);
        //this.$router.push("/logout");
      } finally {
        this.loadingCourses = false; // Stop loading
        this.checkingEnrollment = false; // Stop enrollment check
      }
    },
    // Method to find and play video by ID from URL parameter
    async playVideoById(videoId) {
      // Find the video in the course structure
      for (const unit of this.units) {
        for (const chapter of unit.chapters) {
          const foundClass = chapter.classes.find((cls) => cls.id == videoId);
          if (foundClass) {
            // Wait for the next tick to ensure the DOM is updated
            await this.$nextTick();
            this.playClass(foundClass);
            return;
          }
        }
      }
      console.warn("Video not found with ID:", videoId);
    },

    async joinLiveStream(course_class) {
      try {
        const response = await fetch(
          `${apiUrl}/livestream/token/` + course_class.id,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-country-code": window.appRegion.phone_code,
              Authorization: `Bearer ${this.token}`,
            },
          }
        );

        let data = await response.json();

        if (data.token) {
          // تحويل المستخدم إلى رابط البث مع التوكن
          window.open(
            `https://live.academia.ly/?token=${data.token}&classId=${course_class.id}&access_token=${this.token}`,
            "_blank"
          );
        } else {
          alert(this.$t("livestream.token_failed"));
        }
      } catch (error) {
        console.error("Error fetching token:", error);
        alert(this.$t("common.server_error_generic"));
      }
    },
  },
};
</script>

<style scoped>
/* ===== Course Details Page ===== */
.course-details-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

/* ===== Loading State ===== */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #00c744;
  font-size: 1.1rem;
  font-weight: 600;
}

/* ===== Hero Section ===== */
.course-hero {
  position: relative;
  min-height: 400px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 50, 20, 0.8) 100%
  );
}

.hero-content {
  position: relative;
  z-index: 10;
  padding: 60px 0;
}

.hero-info {
  max-width: 800px;
}

.course-badges-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.course-category-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 199, 68, 0.2);
  color: #4ade80;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.course-institute-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(232, 255, 241, 0.25);
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.course-institute-badge:hover {
  background: rgba(0, 199, 68, 0.3);
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.2);
}

.course-qbank-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(59, 130, 246, 0.25);
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(59, 130, 246, 0.5);
}

.course-qbank-badge:hover {
  background: rgba(59, 130, 246, 0.4);
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(59, 130, 246, 0.3);
}

.course-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 15px;
  line-height: 1.3;
}

.course-description {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.1rem;
  margin-bottom: 25px;
  line-height: 1.6;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
}

.meta-item i {
  color: #4ade80;
  font-size: 1.1rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.share-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  transform: translateY(-2px);
}

.share-btn.share-web {
  background: rgba(0, 199, 68, 0.3);
  border-color: rgba(0, 199, 68, 0.5);
}

.create-bundle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.4);
}

.create-bundle-btn:hover {
  background: linear-gradient(135deg, #00d94d 0%, #00c744 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.5);
}

.create-bundle-btn i {
  font-size: 1rem;
}

/* ===== Main Content Area ===== */
.course-main-content {
  margin-top: -60px;
  position: relative;
  z-index: 20;
  padding-bottom: 60px;
}

.content-grid {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 30px;
}

/* ===== Sidebar ===== */
.course-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Price Card */
.price-card {
  background: white;
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
}

.price-header {
  margin-bottom: 20px;
}

.price-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-current {
  font-size: 2.2rem;
  font-weight: 800;
  color: #00c744;
}

.price-old {
  font-size: 1.1rem;
  color: #a0aec0;
  text-decoration: line-through;
}

.price-free {
  font-size: 2rem;
  font-weight: 800;
  color: #00c744;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  padding: 10px 20px;
  border-radius: 12px;
  display: inline-block;
}

.discount-badge {
  display: inline-block;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.enrolled-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  color: #00c744;
  padding: 15px 20px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
}

.enrolled-badge i {
  font-size: 1.3rem;
}

/* Chat Group Section */
.chat-group-section {
  margin-top: 15px;
}

.chat-group-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chat-group-btn--enter {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.35);
}

.chat-group-btn--enter:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(59, 130, 246, 0.45);
}

.chat-group-btn--join {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.35);
}

.chat-group-btn--join:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(139, 92, 246, 0.45);
}

.chat-group-btn--join:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.chat-group-btn--loading {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.35);
  cursor: wait;
}

.chat-group-btn i {
  font-size: 1.1rem;
}

/* Enrollment Check Loader */
.enrollment-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  border-radius: 12px;
}

.enrollment-spinner {
  display: flex;
  align-items: center;
  gap: 6px;
}

.spinner-dot {
  width: 10px;
  height: 10px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.spinner-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner-dot:nth-child(2) {
  animation-delay: -0.16s;
}

.spinner-dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.checking-text {
  color: #00c744;
  font-size: 0.95rem;
  font-weight: 600;
}

/* Button Loader */
.btn-loader-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50px;
}

.btn-loader {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-loader span {
  width: 8px;
  height: 8px;
  background: #00c744;
  border-radius: 50%;
  animation: btn-bounce 1.4s ease-in-out infinite both;
}

.btn-loader span:nth-child(1) {
  animation-delay: -0.32s;
}

.btn-loader span:nth-child(2) {
  animation-delay: -0.16s;
}

.btn-loader span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes btn-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.enroll-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.enroll-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.4);
}

.enroll-btn i {
  font-size: 1.2rem;
  color: white;
}

.enroll-btn.free-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
}

.enroll-btn.free-btn:hover {
  box-shadow: 0 15px 40px rgba(139, 92, 246, 0.4);
}

/* Instructor Card */
.instructor-card {
  background: white;
  border-radius: 24px;
  padding: 25px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
}

.instructor-header h3 {
  color: #2d3748;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e8fff1;
}

.instructor-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.instructor-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #00c744;
}

.instructor-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.instructor-details {
  flex: 1;
}

.instructor-name {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.instructor-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  color: #00c744;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* ===== Course Content Area ===== */
.course-content-area {
  background: white;
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
}

/* Tabs */
.course-tabs {
  margin-bottom: 30px;
}

.course-tabs .nav-pills {
  display: flex;
  gap: 10px;
  background: #f8fffe;
  padding: 8px;
  border-radius: 16px;
}

.course-tabs .nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  color: #718096;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
}

.course-tabs .nav-link:hover {
  background: #e8fff1;
  color: #00c744;
}

.course-tabs .nav-link.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 5px 20px rgba(0, 199, 68, 0.3);
}

.course-tabs .nav-link.active i {
  color: white;
}

.course-tabs .nav-link i {
  font-size: 1rem;
}

/* Overview Card */
.overview-card {
  padding: 20px 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 25px;
}

.section-title i {
  color: #00c744;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  padding: 20px;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  color: white;
  font-size: 1.3rem;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
}

.stat-label {
  color: #718096;
  font-size: 0.85rem;
}

/* Lectures Card */
.lectures-card {
  padding: 10px 0;
}

/* Unit Section - New Design */
.unit-section {
  margin-bottom: 30px;
  background: white;
  border-radius: 20px;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.unit-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 6px 18px;
}

.unit-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.unit-number {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.3rem;
  font-weight: 800;
}

.unit-title-wrapper {
  flex: 1;
}

.unit-title {
  color: white;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 5px 0;
}

.unit-duration {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: white;
  font-size: 0.85rem;
}

.unit-duration i {
  color: white !important;
}

/* Chapters List */
.chapters-list {
  padding: 15px;
}

.chapter-item {
  background: #f8fffe;
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.chapter-item:last-child {
  margin-bottom: 0;
}

.chapter-item:hover {
  box-shadow: 0 5px 20px rgba(0, 199, 68, 0.1);
}

.chapter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chapter-header:hover {
  background: #e8fff1;
}

.chapter-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.chapter-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  font-size: 1rem;
}

.chapter-details {
  flex: 1;
}

.chapter-title {
  display: block;
  color: #2d3748;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.chapter-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #718096;
  font-size: 0.8rem;
}

.chapter-meta i {
  color: #00c744;
}

.classes-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.chapter-arrow {
  color: #00c744;
  font-size: 0.9rem;
  transition: transform 0.3s ease;
}

.chapter-header[aria-expanded="true"] .chapter-arrow,
.chapter-header:not(.collapsed) .chapter-arrow {
  transform: rotate(180deg);
}

/* Classes List */
.classes-list {
  padding: 0 15px 15px;
}

.class-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
  border: 1px solid #e8fff1;
  transition: all 0.3s ease;
}

.class-item:last-child {
  margin-bottom: 0;
}

.class-item:hover {
  border-color: #00c744;
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.1);
}

.class-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.class-type-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.class-type-icon i {
  color: white !important;
}

.class-title {
  color: #2d3748;
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.class-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.class-duration {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #718096;
  font-size: 0.8rem;
  background: #f8fffe;
  padding: 5px 10px;
  border-radius: 20px;
}

.class-duration i {
  color: #00c744;
  font-size: 0.75rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
}

.action-btn:hover {
  transform: scale(1.1);
}

.play-btn {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white !important;
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.3);
}

.play-btn i {
  color: white !important;
}

.link-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white !important;
  box-shadow: 0 5px 15px rgba(59, 130, 246, 0.3);
}

.link-btn i {
  color: white !important;
}

.live-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white !important;
  padding: 8px 14px;
  width: auto;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 5px;
  box-shadow: 0 5px 15px rgba(239, 68, 68, 0.3);
}

.live-btn i {
  color: white !important;
  animation: pulse 1.5s infinite;
}

.pdf-btn {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white !important;
  box-shadow: 0 5px 15px rgba(249, 115, 22, 0.3);
}

.pdf-btn i {
  color: white !important;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Video Loader Styles */
.video-loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.video-loader-content {
  text-align: center;
}

.video-loader-spinner {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto 25px;
}

.video-loader-spinner .spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: video-spin 1.2s linear infinite;
}

.video-loader-spinner .spinner-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-top-color: #4ade80;
  animation-duration: 1s;
  animation-direction: reverse;
}

.video-loader-spinner .spinner-ring:nth-child(3) {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-top-color: #86efac;
  animation-duration: 0.8s;
}

.video-loader-spinner .play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 20px rgba(0, 199, 68, 0.4);
}

.video-loader-spinner .play-icon i {
  color: white !important;
  font-size: 0.9rem;
  margin-left: 3px;
}

@keyframes video-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.video-loader-text {
  color: #4ade80;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  animation: video-pulse 1.5s ease-in-out infinite;
}

@keyframes video-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Video Player Card Styles - Premium */
.video-player-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 199, 68, 0.08), 0 15px 50px rgba(0, 0, 0, 0.12),
    0 8px 25px rgba(0, 199, 68, 0.08);
  margin: 15px;
}

.video-player-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px 28px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #009934 100%);
  position: relative;
  overflow: hidden;
}

.video-player-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
  background-size: 200% 100%;
  animation: headerShimmer 4s infinite;
}

@keyframes headerShimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.video-title-wrapper {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;
}

.video-title-wrapper i {
  color: white;
  font-size: 1.6rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.video-class-title {
  margin: 0;
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.02em;
}

.video-player-body {
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: linear-gradient(180deg, #fafffe 0%, #f5fff9 100%);
}

.video-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* Speed Control Styles - Premium */
.speed-control {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(12px);
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.speed-control:hover {
  background: rgba(255, 255, 255, 0.28);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.speed-icon {
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.speed-icon i {
  color: #00c744 !important;
  font-size: 0.9rem;
}

.speed-label {
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.speed-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.speed-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: white;
  color: #00c744;
  border: none;
  padding: 10px 38px 10px 16px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.speed-select:hover {
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.35);
  transform: scale(1.03);
}

.speed-select option {
  background: #1e293b;
  color: white;
  padding: 12px;
}

.speed-arrow {
  position: absolute;
  right: 14px;
  color: #00c744 !important;
  font-size: 0.75rem;
  pointer-events: none;
  transition: transform 0.3s ease;
}

.speed-select:focus + .speed-arrow {
  transform: rotate(180deg);
}

/* Dark Mode for Video Player */
[data-bs-theme="dark"] .video-player-card,
[data-theme="dark"] .video-player-card,
.dark .video-player-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .video-player-header,
[data-theme="dark"] .video-player-header,
.dark .video-player-header {
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 100%);
  border-color: rgba(74, 222, 128, 0.2);
}

[data-bs-theme="dark"] .video-class-title,
[data-theme="dark"] .video-class-title,
.dark .video-class-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .video-player-body,
[data-theme="dark"] .video-player-body,
.dark .video-player-body {
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 100%);
}

[data-bs-theme="dark"] .speed-control,
[data-theme="dark"] .speed-control,
.dark .speed-control {
  background: #0f172a;
  border-color: rgba(74, 222, 128, 0.3);
}

[data-bs-theme="dark"] .speed-label,
[data-theme="dark"] .speed-label,
.dark .speed-label {
  color: #f1f5f9;
}

/* Legacy time-duration for compatibility */
.time-duration {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: white;
  color: #00c744;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Legacy play-video for compatibility */
.play-video {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
  color: white !important;
  border-radius: 50%;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  padding: 0 !important;
}

.play-video:hover {
  transform: scale(1.1);
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.4);
}

/* Custom Checkbox */
.custom-checkbox {
  margin: 5px;
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 22px;
  height: 22px;
  border: 2px solid #00c744;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease-in-out;
  margin-right: 8px;
  vertical-align: middle;
  background-color: #fff;
}

.custom-checkbox:checked {
  background-color: #00c744;
  border-color: #00c744;
}

.custom-checkbox:checked::after {
  content: "✔";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #fff;
}

.custom-checkbox:hover {
  box-shadow: 0 0 8px rgba(0, 199, 68, 0.5);
}

/* ===== Dark Mode ===== */
[data-bs-theme="dark"] .course-details-page,
[data-theme="dark"] .course-details-page,
.dark .course-details-page {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .loading-container,
[data-theme="dark"] .loading-container,
.dark .loading-container {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .price-card,
[data-theme="dark"] .price-card,
.dark .price-card,
[data-bs-theme="dark"] .instructor-card,
[data-theme="dark"] .instructor-card,
.dark .instructor-card,
[data-bs-theme="dark"] .course-content-area,
[data-theme="dark"] .course-content-area,
.dark .course-content-area {
  background: #1e293b;
}

[data-bs-theme="dark"] .price-free,
[data-theme="dark"] .price-free,
.dark .price-free,
[data-bs-theme="dark"] .enrolled-badge,
[data-theme="dark"] .enrolled-badge,
.dark .enrolled-badge {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .enrollment-loader,
[data-theme="dark"] .enrollment-loader,
.dark .enrollment-loader,
[data-bs-theme="dark"] .btn-loader-wrapper,
[data-theme="dark"] .btn-loader-wrapper,
.dark .btn-loader-wrapper {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .chat-group-btn--enter,
[data-theme="dark"] .chat-group-btn--enter,
.dark .chat-group-btn--enter {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.25);
}

[data-bs-theme="dark"] .chat-group-btn--join,
[data-theme="dark"] .chat-group-btn--join,
.dark .chat-group-btn--join {
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  box-shadow: 0 8px 25px rgba(124, 58, 237, 0.25);
}

[data-bs-theme="dark"] .checking-text,
[data-theme="dark"] .checking-text,
.dark .checking-text {
  color: #4ade80;
}

[data-bs-theme="dark"] .instructor-header h3,
[data-theme="dark"] .instructor-header h3,
.dark .instructor-header h3,
[data-bs-theme="dark"] .instructor-name,
[data-theme="dark"] .instructor-name,
.dark .instructor-name,
[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title,
[data-bs-theme="dark"] .stat-value,
[data-theme="dark"] .stat-value,
.dark .stat-value,
[data-bs-theme="dark"] .unit-section h3,
[data-theme="dark"] .unit-section h3,
.dark .unit-section h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .course-tabs,
[data-theme="dark"] .course-tabs,
.dark .course-tabs {
  background: transparent;
}

[data-bs-theme="dark"] .course-tabs .nav-pills,
[data-theme="dark"] .course-tabs .nav-pills,
.dark .course-tabs .nav-pills {
  background: #1e293b;
  border: 1px solid #334155;
}

[data-bs-theme="dark"] .course-tabs .nav-item,
[data-theme="dark"] .course-tabs .nav-item,
.dark .course-tabs .nav-item {
  background: transparent;
}

[data-bs-theme="dark"] .course-tabs .nav-link,
[data-theme="dark"] .course-tabs .nav-link,
.dark .course-tabs .nav-link {
  color: #94a3b8 !important;
  background: transparent !important;
}

[data-bs-theme="dark"] .course-tabs .nav-link i,
[data-theme="dark"] .course-tabs .nav-link i,
.dark .course-tabs .nav-link i {
  color: #64748b !important;
}

[data-bs-theme="dark"] .course-tabs .nav-link:hover,
[data-theme="dark"] .course-tabs .nav-link:hover,
.dark .course-tabs .nav-link:hover {
  background: #0f3d1c !important;
  color: #4ade80 !important;
}

[data-bs-theme="dark"] .course-tabs .nav-link:hover i,
[data-theme="dark"] .course-tabs .nav-link:hover i,
.dark .course-tabs .nav-link:hover i {
  color: #4ade80 !important;
}

[data-bs-theme="dark"] .course-tabs .nav-link.active,
[data-theme="dark"] .course-tabs .nav-link.active,
.dark .course-tabs .nav-link.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
  color: white !important;
}

[data-bs-theme="dark"] .course-tabs .nav-link.active i,
[data-theme="dark"] .course-tabs .nav-link.active i,
.dark .course-tabs .nav-link.active i {
  color: white !important;
}

[data-bs-theme="dark"] .stat-item,
[data-theme="dark"] .stat-item,
.dark .stat-item {
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 100%);
}

/* Dark Mode for New Lectures Design */
[data-bs-theme="dark"] .unit-section,
[data-theme="dark"] .unit-section,
.dark .unit-section {
  background: #1e293b;
}

[data-bs-theme="dark"] .chapters-list,
[data-theme="dark"] .chapters-list,
.dark .chapters-list {
  background: #1e293b;
}

[data-bs-theme="dark"] .chapter-item,
[data-theme="dark"] .chapter-item,
.dark .chapter-item {
  background: #0f172a;
}

[data-bs-theme="dark"] .chapter-header:hover,
[data-theme="dark"] .chapter-header:hover,
.dark .chapter-header:hover {
  background: #1a2744;
}

[data-bs-theme="dark"] .chapter-title,
[data-theme="dark"] .chapter-title,
.dark .chapter-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .chapter-icon,
[data-theme="dark"] .chapter-icon,
.dark .chapter-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
}

[data-bs-theme="dark"] .class-item,
[data-theme="dark"] .class-item,
.dark .class-item {
  background: #1e293b;
  border-color: #2d3748;
}

[data-bs-theme="dark"] .class-item:hover,
[data-theme="dark"] .class-item:hover,
.dark .class-item:hover {
  border-color: #4ade80;
  background: #1a2744;
}

[data-bs-theme="dark"] .class-title,
[data-theme="dark"] .class-title,
.dark .class-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .class-duration,
[data-theme="dark"] .class-duration,
.dark .class-duration {
  background: #0f172a;
  color: #94a3b8;
}

[data-bs-theme="dark"] .time-duration,
[data-theme="dark"] .time-duration,
.dark .time-duration {
  background: #0f172a;
  color: #4ade80;
}

[data-bs-theme="dark"] .instructor-badge,
[data-theme="dark"] .instructor-badge,
.dark .instructor-badge {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
}

[data-bs-theme="dark"] .course-institute-badge,
[data-theme="dark"] .course-institute-badge,
.dark .course-institute-badge {
  background: rgba(74, 222, 128, 0.15);
  border-color: rgba(74, 222, 128, 0.4);
  color: #4ade80;
}

[data-bs-theme="dark"] .course-institute-badge:hover,
[data-theme="dark"] .course-institute-badge:hover,
.dark .course-institute-badge:hover {
  background: rgba(74, 222, 128, 0.25);
  color: #4ade80;
}

[data-bs-theme="dark"] .course-qbank-badge,
[data-theme="dark"] .course-qbank-badge,
.dark .course-qbank-badge {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.5);
  color: #60a5fa;
}

[data-bs-theme="dark"] .course-qbank-badge:hover,
[data-theme="dark"] .course-qbank-badge:hover,
.dark .course-qbank-badge:hover {
  background: rgba(59, 130, 246, 0.35);
  color: #60a5fa;
}

[data-bs-theme="dark"] .custom-checkbox,
[data-theme="dark"] .custom-checkbox,
.dark .custom-checkbox {
  background-color: #0f172a;
  border-color: #4ade80;
}

/* ===== Create Bundle Card ===== */
.create-bundle-card {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f5e3 100%);
  border-radius: 20px;
  border: 2px solid #00c444;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.15);
}
.bundle-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.bundle-card-header i {
  font-size: 1.5rem;
  color: #00a43c;
}
.bundle-card-header span {
  font-weight: 700;
  color: #00a43c;
  font-size: 1.1rem;
}
.bundle-card-desc {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.5;
}
.create-bundle-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}
.create-bundle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.4);
}

/* ===== Custom Bundle Modal Styles ===== */
#customBundleModal .modal-content.custom-bundle-modal {
  border: none;
  border-radius: 24px;
  overflow: hidden;
}
#customBundleModal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  padding: 20px 24px;
}
#customBundleModal .modal-title {
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
}
#customBundleModal .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}
#customBundleModal .btn-close:hover {
  opacity: 1;
}
#customBundleModal .modal-body {
  padding: 24px;
  background: #f8fafc;
  max-height: 60vh;
  overflow-y: scroll;
}
#customBundleModal .modal-body::-webkit-scrollbar {
  width: 12px !important;
  display: block !important;
}
#customBundleModal .modal-body::-webkit-scrollbar-track {
  background: #e8fff1 !important;
  border-radius: 6px !important;
}
#customBundleModal .modal-body::-webkit-scrollbar-thumb {
  background: #00c744 !important;
  border-radius: 6px !important;
}
.custom-bundle-loading,
.custom-bundle-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
}
.custom-bundle-loading .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e8fff1;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.custom-bundle-empty i {
  font-size: 3rem;
  margin-bottom: 15px;
  color: #cbd5e1;
}
.custom-bundle-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}
.custom-bundle-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.custom-bundle-card.selected {
  border-color: #00c744;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.2);
}
.bundle-card-image {
  position: relative;
  height: 120px;
  overflow: hidden;
}
.bundle-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bundle-card-price {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 199, 68, 0.9);
  color: white;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
}
.bundle-card-content {
  padding: 12px;
}
.bundle-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bundle-card-instructor {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}
.bundle-card-actions {
  padding: 12px;
  border-top: 1px solid #e2e8f0;
}
.bundle-add-btn,
.bundle-remove-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}
.bundle-add-btn {
  background: #e8fff1;
  color: #00a43c;
}
.bundle-add-btn:hover {
  background: #d4f5e3;
}
.bundle-remove-btn {
  background: #fee2e2;
  color: #dc2626;
}
.bundle-remove-btn:hover {
  background: #fecaca;
}
.custom-bundle-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}
.pagination-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8fff1;
  border: none;
  border-radius: 8px;
  color: #00a43c;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-btn:hover:not(:disabled) {
  background: #00c744;
  color: white;
}
.pagination-info {
  font-size: 0.9rem;
  color: #64748b;
}
.custom-bundle-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 20px 24px;
}
.bundle-summary {
  background: white;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.9rem;
  color: #64748b;
}
.summary-row.discount {
  color: #16a34a;
}
.summary-row.total {
  border-top: 1px solid #e2e8f0;
  margin-top: 8px;
  padding-top: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
}
.summary-value {
  font-weight: 600;
}
.bundle-enroll-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}
.bundle-enroll-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.4);
}
.bundle-enroll-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.min-courses-hint {
  text-align: center;
  font-size: 0.85rem;
  color: #f59e0b;
  margin-top: 10px;
}

/* Bundle Continue Button (Step 1) */
.bundle-continue-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}
.bundle-continue-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}
.bundle-continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Step 2: Review Body */
.bundle-review-body {
  padding: 20px 24px;
  background: #f8fafc;
}
.bundle-review-header {
  margin-bottom: 20px;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.back-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Selected Courses List */
.selected-courses-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}
.selected-course-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.selected-course-image {
  width: 80px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
}
.selected-course-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.selected-course-info {
  flex: 1;
  min-width: 0;
}
.selected-course-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-course-instructor {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
}
.selected-course-price {
  font-size: 1rem;
  font-weight: 700;
  color: #00c744;
  white-space: nowrap;
}
.remove-course-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee2e2;
  color: #ef4444;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.remove-course-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* Order Summary */
.bundle-order-summary {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.order-summary-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
}
.order-summary-title i {
  color: #00c744;
}
.order-summary-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.order-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
  color: #475569;
}
.order-row.discount {
  color: #f59e0b;
}
.order-row.total {
  padding-top: 12px;
  border-top: 2px dashed #e2e8f0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #00c744;
}

/* ===== Responsive ===== */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .course-sidebar {
    order: -1;
  }
}

@media (max-width: 768px) {
  .course-hero {
    min-height: 350px;
  }

  .hero-content {
    padding: 40px 20px;
  }

  .course-title {
    font-size: 1.8rem;
  }

  .course-meta {
    gap: 15px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .price-card,
  .instructor-card,
  .course-content-area {
    padding: 20px;
    border-radius: 20px;
  }
}
</style>

<style>
/* ===== Non-scoped CSS for dynamically injected content ===== */

/* Video Modal Dialog Size - Compact & Balanced */
#video-modal .modal-dialog {
  max-width: 900px !important;
  width: 90vw !important;
  margin: 1.5rem auto !important;
}

@media (min-width: 1200px) {
  #video-modal .modal-dialog {
    max-width: 950px !important;
  }
}

@media (max-width: 768px) {
  #video-modal .modal-dialog {
    max-width: 96vw !important;
    margin: 0.75rem auto !important;
  }
}

@media (max-width: 480px) {
  #video-modal .modal-dialog {
    max-width: 100vw !important;
    margin: 0.5rem !important;
  }
}

/* Video Modal Content */
#video-modal .modal-content {
  border: none !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 0 0 1px rgba(0, 199, 68, 0.08), 0 20px 60px rgba(0, 0, 0, 0.2),
    0 8px 25px rgba(0, 199, 68, 0.1) !important;
}

/* Video Modal Header */
#video-modal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
  padding: 14px 20px !important;
  border: none !important;
}

#video-modal .modal-header .modal-title {
  color: white !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
}

/* Video Modal Body */
#video-modal .modal-body {
  padding: 0 !important;
  background: #fafffe !important;
}

/* Video Section Container */
#video-section {
  background: #fafffe !important;
  padding: 12px !important;
}

/* Video Player Card - Dynamic Content */
#video-section .video-player-card {
  background: white !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  margin: 0 !important;
}

#video-section .video-player-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  flex-wrap: wrap !important;
  gap: 12px !important;
  padding: 12px 16px !important;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
}

#video-section .video-title-wrapper {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
}

#video-section .video-title-wrapper i {
  color: white !important;
  font-size: 1.2rem !important;
}

#video-section .video-class-title {
  margin: 0 !important;
  color: white !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
}

#video-section .video-player-body {
  position: relative !important;
  padding-top: 56.25% !important;
  background: #f8fffe !important;
}

#video-section .video-iframe {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border: none !important;
}

/* Speed Control - Compact */
#video-section .speed-control {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding: 8px 12px !important;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 30px !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
}

#video-section .speed-icon {
  width: 28px !important;
  height: 28px !important;
  background: white !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

#video-section .speed-icon i {
  color: #00c744 !important;
  font-size: 0.75rem !important;
}

#video-section .speed-label {
  color: white !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

#video-section .speed-select-wrapper {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
}

#video-section .speed-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  background: white !important;
  color: #00c744 !important;
  border: none !important;
  padding: 6px 28px 6px 12px !important;
  border-radius: 20px !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  cursor: pointer !important;
  outline: none !important;
}

#video-section .speed-arrow {
  position: absolute !important;
  right: 10px !important;
  color: #00c744 !important;
  font-size: 0.65rem !important;
  pointer-events: none !important;
}

/* Video Loader - Compact */
#video-loader .video-loader-container {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 350px !important;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%) !important;
  border-radius: 12px !important;
}

#video-loader .video-loader-content {
  text-align: center !important;
}

#video-loader .video-loader-spinner {
  position: relative !important;
  width: 70px !important;
  height: 70px !important;
  margin: 0 auto 18px !important;
}

#video-loader .spinner-ring {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border: 2px solid transparent !important;
  border-top-color: #00c744 !important;
  border-radius: 50% !important;
  animation: video-spin 1.2s linear infinite !important;
}

#video-loader .spinner-ring:nth-child(2) {
  width: 80% !important;
  height: 80% !important;
  top: 10% !important;
  left: 10% !important;
  border-top-color: #4ade80 !important;
  animation-duration: 1s !important;
  animation-direction: reverse !important;
}

#video-loader .spinner-ring:nth-child(3) {
  width: 60% !important;
  height: 60% !important;
  top: 20% !important;
  left: 20% !important;
  border-top-color: #86efac !important;
  animation-duration: 0.8s !important;
}

#video-loader .play-icon {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 30px !important;
  height: 30px !important;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

#video-loader .play-icon i {
  color: white !important;
  font-size: 0.7rem !important;
  margin-left: 2px !important;
}

#video-loader .video-loader-text {
  color: #4ade80 !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

@keyframes video-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive for Video Modal */
@media (max-width: 768px) {
  #video-section {
    padding: 8px !important;
  }

  #video-section .video-player-header {
    padding: 10px 14px !important;
    gap: 10px !important;
  }

  #video-section .video-class-title {
    font-size: 0.9rem !important;
  }

  #video-section .video-title-wrapper i {
    font-size: 1rem !important;
  }

  #video-section .speed-control {
    padding: 6px 10px !important;
    gap: 6px !important;
  }

  #video-section .speed-label {
    display: none !important;
  }

  #video-section .speed-icon {
    width: 24px !important;
    height: 24px !important;
  }

  #video-loader .video-loader-container {
    min-height: 250px !important;
  }

  #video-loader .video-loader-spinner {
    width: 50px !important;
    height: 50px !important;
  }
}

@media (max-width: 480px) {
  #video-section {
    padding: 6px !important;
  }

  #video-section .video-player-card {
    border-radius: 8px !important;
  }

  #video-section .video-player-header {
    padding: 8px 12px !important;
  }

  #video-section .video-class-title {
    font-size: 0.85rem !important;
  }
}

/* ===== Dark Mode Support for Video Modal ===== */
[data-bs-theme="dark"] #video-modal .modal-content,
[data-theme="dark"] #video-modal .modal-content,
.dark #video-modal .modal-content {
  background: #1e293b !important;
  box-shadow: 0 0 0 1px rgba(0, 199, 68, 0.15), 0 20px 60px rgba(0, 0, 0, 0.5) !important;
}

[data-bs-theme="dark"] #video-modal .modal-body,
[data-theme="dark"] #video-modal .modal-body,
.dark #video-modal .modal-body {
  background: #0f172a !important;
}

[data-bs-theme="dark"] #video-section,
[data-theme="dark"] #video-section,
.dark #video-section {
  background: #0f172a !important;
}

[data-bs-theme="dark"] #video-section .video-player-card,
[data-theme="dark"] #video-section .video-player-card,
.dark #video-section .video-player-card {
  background: #1e293b !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
}

[data-bs-theme="dark"] #video-section .video-player-body,
[data-theme="dark"] #video-section .video-player-body,
.dark #video-section .video-player-body {
  background: #0f172a !important;
}

[data-bs-theme="dark"] #video-section .speed-select,
[data-theme="dark"] #video-section .speed-select,
.dark #video-section .speed-select {
  background: #334155 !important;
  color: #4ade80 !important;
}

[data-bs-theme="dark"] #video-section .speed-arrow,
[data-theme="dark"] #video-section .speed-arrow,
.dark #video-section .speed-arrow {
  color: #4ade80 !important;
}

[data-bs-theme="dark"] #video-section .speed-icon,
[data-theme="dark"] #video-section .speed-icon,
.dark #video-section .speed-icon {
  background: #334155 !important;
}

[data-bs-theme="dark"] #video-section .speed-icon i,
[data-theme="dark"] #video-section .speed-icon i,
.dark #video-section .speed-icon i {
  color: #4ade80 !important;
}

[data-bs-theme="dark"] #video-section .speed-label,
[data-theme="dark"] #video-section .speed-label,
.dark #video-section .speed-label {
  color: #e2e8f0 !important;
}

[data-bs-theme="dark"] #video-section .speed-control,
[data-theme="dark"] #video-section .speed-control,
.dark #video-section .speed-control {
  background: rgba(15, 23, 42, 0.6) !important;
  border-color: rgba(74, 222, 128, 0.3) !important;
}

[data-bs-theme="dark"] #video-loader .video-loader-container,
[data-theme="dark"] #video-loader .video-loader-container,
.dark #video-loader .video-loader-container {
  background: linear-gradient(135deg, #0a0f1a 0%, #0f172a 100%) !important;
}

/* Dark Mode for Modal Backdrop */
[data-bs-theme="dark"] .modal-backdrop,
[data-theme="dark"] .modal-backdrop,
.dark .modal-backdrop {
  background-color: #000 !important;
}

/* Dark Mode for Action Buttons in Header */
[data-bs-theme="dark"] .action-btn,
[data-theme="dark"] .action-btn,
.dark .action-btn {
  background: rgba(15, 23, 42, 0.8) !important;
}

[data-bs-theme="dark"] .action-btn:hover,
[data-theme="dark"] .action-btn:hover,
.dark .action-btn:hover {
  background: rgba(15, 23, 42, 0.95) !important;
}

/* Dark Mode for Class Actions */
[data-bs-theme="dark"] .class-actions .action-btn,
[data-theme="dark"] .class-actions .action-btn,
.dark .class-actions .action-btn {
  background: #0f172a !important;
  color: #4ade80 !important;
}

[data-bs-theme="dark"] .class-actions .action-btn:hover,
[data-theme="dark"] .class-actions .action-btn:hover,
.dark .class-actions .action-btn:hover {
  background: #1a2744 !important;
}

[data-bs-theme="dark"] .class-actions .action-btn i,
[data-theme="dark"] .class-actions .action-btn i,
.dark .class-actions .action-btn i {
  color: #4ade80 !important;
}

/* Dark Mode for PDF Button */
[data-bs-theme="dark"] .pdf-btn,
[data-theme="dark"] .pdf-btn,
.dark .pdf-btn {
  background: linear-gradient(135deg, #7c2d12 0%, #9a3412 100%) !important;
}

/* Dark Mode for Live Button */
[data-bs-theme="dark"] .live-btn,
[data-theme="dark"] .live-btn,
.dark .live-btn {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%) !important;
}

/* Dark Mode for Link Button */
[data-bs-theme="dark"] .link-btn,
[data-theme="dark"] .link-btn,
.dark .link-btn {
  background: linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%) !important;
}

/* Dark Mode for Course Description */
[data-bs-theme="dark"] .course-description,
[data-theme="dark"] .course-description,
.dark .course-description {
  color: #cbd5e1 !important;
}

[data-bs-theme="dark"] .course-description p,
[data-theme="dark"] .course-description p,
.dark .course-description p {
  color: #cbd5e1 !important;
}

/* Dark Mode for Stat Labels */
[data-bs-theme="dark"] .stat-label,
[data-theme="dark"] .stat-label,
.dark .stat-label {
  color: #94a3b8 !important;
}

/* Dark Mode for Instructor Bio */
[data-bs-theme="dark"] .instructor-bio,
[data-theme="dark"] .instructor-bio,
.dark .instructor-bio {
  color: #94a3b8 !important;
}

/* Dark Mode for Chapter Meta */
[data-bs-theme="dark"] .chapter-meta,
[data-theme="dark"] .chapter-meta,
.dark .chapter-meta {
  color: #64748b !important;
}

/* Dark Mode for Empty State */
[data-bs-theme="dark"] .empty-state,
[data-theme="dark"] .empty-state,
.dark .empty-state {
  background: #1e293b !important;
  color: #94a3b8 !important;
}

/* Create Bundle Button - Sidebar Style */
.create-bundle-btn-sidebar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-top: 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px dashed #00c744;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.create-bundle-btn-sidebar:hover {
  background: linear-gradient(135deg, #00c744 0%, #00a838 100%);
  border-style: solid;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

.create-bundle-btn-sidebar:hover .bundle-btn-title,
.create-bundle-btn-sidebar:hover .bundle-btn-subtitle,
.create-bundle-btn-sidebar:hover .bundle-btn-icon i,
.create-bundle-btn-sidebar:hover .bundle-btn-arrow {
  color: white !important;
}

.bundle-btn-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bundle-btn-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00c744 0%, #00a838 100%);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.bundle-btn-icon i {
  font-size: 1.1rem;
  color: white;
}

.bundle-btn-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.bundle-btn-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #166534;
}

.bundle-btn-subtitle {
  font-size: 0.75rem;
  color: #22c55e;
}

.bundle-btn-arrow {
  color: #00c744;
  font-size: 0.9rem;
  transition: transform 0.3s ease;
}

.create-bundle-btn-sidebar:hover .bundle-btn-arrow {
  transform: translateX(-4px);
}

.create-bundle-btn-sidebar:hover .bundle-btn-icon {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
}

/* Dark Mode for Create Bundle Button */
[data-bs-theme="dark"] .create-bundle-btn-sidebar,
[data-theme="dark"] .create-bundle-btn-sidebar,
.dark .create-bundle-btn-sidebar {
  background: linear-gradient(135deg, #052e16 0%, #14532d 100%);
  border-color: #22c55e;
}

[data-bs-theme="dark"] .bundle-btn-title,
[data-theme="dark"] .bundle-btn-title,
.dark .bundle-btn-title {
  color: #86efac;
}

[data-bs-theme="dark"] .bundle-btn-subtitle,
[data-theme="dark"] .bundle-btn-subtitle,
.dark .bundle-btn-subtitle {
  color: #4ade80;
}
</style>
